// Componentes nativos
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Label } from "recharts";
import Modal from "react-modal";

// Componentes personalizados
import Header from "../../Header";

// Ícones
import { IoMdArrowRoundBack } from "react-icons/io";
import { BsFillGearFill } from "react-icons/bs";

// Classes de controle e de armazenamento
import OffStorage, { DadosUA } from "../Core/OffStorage";
import ELV, { ComposicaoUA } from "../Core/ELV";
import Substancia from "../Core/Substancia";

// Tipagem intermediária do gráfico
type DataGraph = {
    "Fração": number,
    "BOLT": number,
    "ORVT": number,
    "BOLP": number,
    "ORVP": number
};

export default function DiagramaELV() {
    // Gerencia a navegação
    const nav = useHistory();
    function back() {
        nav.goBack();
    }

    // Cria a instância do banco offline
    const banco = new OffStorage();

    // Gerencia as inforamções em cada lista de substâncias
    const [substancias, setSubstancias] = useState<DadosUA[]>([]);
    const [componente1, setComponente1] = useState("");
    const [componente2, setComponente2] = useState("");

    // Gerencia as informações complementares do modal
    const [aberto, setAberto] = useState(false);
    const [temperatura, setTemperatura] = useState("298");
    const [pressao, setPressao] = useState("101.325");
    const [tipo, setTipo] = useState("temperatura");

    // Gerencia informações do gráfico e alerta de erro
    const [carregado, setCarregado] = useState(false);
    const [alerta, setAlerta] = useState("");
    const [dataGraph, setDataGraph] = useState<DataGraph[]>([]);

    // Função de cálculo
    function calcular() {
        // Recupera informações
        const sub1 = find(componente1);
        const sub2 = find(componente2);

        // Cria instâncias de substâncias
        const instSub1 = new Substancia(String(sub1.unifac.substancia?.formula), String(sub1.unifac.substancia?.nome));
        const instSub2 = new Substancia(String(sub2.unifac.substancia?.formula), String(sub2.unifac.substancia?.nome));

        // Cria a instância de cálculo e o esqueleto de composição
        const elv = new ELV();
        const sistema: ComposicaoUA[] = [{
            substancia: instSub1,
            fracao: 0,
            subgrupos: sub1.unifac.subgrupos,
            antoine: sub1.antoine
        }, {
            substancia: instSub2,
            fracao: 1,
            subgrupos: sub2.unifac.subgrupos,
            antoine: sub2.antoine
        }];

        // Define os intervalos
        const limInf = 0;
        const limSup = 1;
        const passos = 100;
        const intervalo = (limSup - limInf) / passos;

        // Calcula os pontos
        const data: DataGraph[] = [];
        for(let i = 0; i <= passos; i++) {
            // Altera as composições
            let x = limInf + intervalo * i;
            sistema[0].fracao = x;
            sistema[1].fracao = 1 - x;

            let bolp = parseFloat(pressao), orvp = parseFloat(pressao);
            let bolt = parseFloat(temperatura), orvt = parseFloat(temperatura);
            if(tipo == "temperatura") {
                bolt = elv.bolt(parseFloat(pressao), sistema).temperatura;
                orvt = elv.orvt(parseFloat(pressao), sistema).temperatura;
            } else if(tipo == "pressao") {
                bolp = elv.bolp(parseFloat(temperatura), sistema).pressao;
                orvp = elv.orvp(parseFloat(temperatura), sistema).pressao;
            }

            data.push({
                "Fração": parseFloat(x.toFixed(2)),
                "BOLP": bolp,
                "BOLT": bolt,
                "ORVP": orvp,
                "ORVT": orvt
            })
        }

        setCarregado(true);
        setDataGraph(data);
    }

    // Função de busca
    function find(nome: string) {
        let dados = substancias[0];
        for(let i = 0; i < substancias.length; i++) {
            if(substancias[i].unifac.substancia?.nome == nome) {
                dados = substancias[i];
                break;
            }
        }
        return dados;
    }

    // Função que atualiza a temperatura
    function alterarTemperatura(event: React.ChangeEvent<HTMLInputElement>) {
        setTemperatura(event.target.value);

        const valor = parseFloat(event.target.value.replace(",", "."));
        if(!isNaN(valor)) {
            if(valor > 0) {
                setTemperatura(valor.toString());
                setAlerta("");
            } else {
                setAlerta("Atenção: Não é possível calcular para o zero absoluto.");
            }
        } else {
            setAlerta("Atenção: Informe um valor válido para a temperatura do sistema.");
        }
    }

    // Função que atualiza a pressão
    function alterarPressao(event: React.ChangeEvent<HTMLInputElement>) {
        setPressao(event.target.value);

        const valor = parseFloat(event.target.value.replace(",", "."));
        if(!isNaN(valor)) {
            if(valor > 0) {
                setPressao(valor.toString());
                setAlerta("");
            } else {
                setAlerta("Atenção: Não é possível calcular para pressão zero ou negativa.");
            }
        } else {
            setAlerta("Atenção: Informe um valor válido para a pressão do sistema.");
        }
    }

    // Função para selecionar os compoentes de cada lista
    function sel1(event: React.ChangeEvent<HTMLSelectElement>) {
        setComponente1(event.target.value);
        setAlerta("");
    }

    function sel2(event: React.ChangeEvent<HTMLSelectElement>) {
        setComponente2(event.target.value);
        setAlerta("");
    }

    function seltipo(event: React.ChangeEvent<HTMLSelectElement>) {
        setTipo(event.target.value);
    }

    // Função de inicialização
    function init() {
        const disponiveis = banco.getDadosUA();
        setSubstancias(disponiveis);
    }

    // Função de renderização e cálculo
    function render() {
        // Controle de erros
        if(alerta != "") {
            setCarregado(false);
            return;
        }
        if(componente1 == "" || componente2 == "") {
            setAlerta("Atenção: Selecione o par de componentes nas listas disponíveis.");
            setCarregado(false);
            return;
        }
        if(componente1 == componente2) {
            setAlerta("Atenção: Selecione componentes diferentes para compor o sistema.");
            setCarregado(false);
            return;
        }

        calcular();
    }

    useEffect(() => {
        init();
    }, []);

    useEffect(() => {
        render();
    },[componente1, componente2, temperatura, pressao, tipo]);

    return (
        <div className="section">
            <Header />

            <div className="content">
                <div className="container">

                    <div className="linha-titulo">
                        <h3>Diagramas de Equilíbrio Líquido-Vapor</h3>

                        <div className="botao-icone" onClick={back}>
                            <IoMdArrowRoundBack size={20} />
                            <span>Voltar</span>
                        </div>
                    </div>

                    <div className="linha-nivel">
                        <span className="nivel"><strong>Nível</strong>: Avançado</span>
                    </div>

                    <p>Nesta seção você poderá criar diagramas de Equilíbrio Líquido-Vapor (ELV) para sistemas binários que você escolher. Para isso, só selecionar os compostos nas listas abaixo.</p>

                    <p>Utilize o botão de "Configurações" para alterar o tipo do gráfico e também o parâmetro fixado, que varia de acordo com o tipo do grafico.</p>

                    <p>OBS.: O gráfico exibe a fração molar na fase líquida do componente 1, ou seja, daquele que você selecionou na primeira lista.</p>

                    <div className="linha-config">
                        <div className="botao-config" onClick={() => setAberto(true)}>
                            <BsFillGearFill size={17} />
                            <span>Configurações</span>
                        </div>
                    </div>

                    <div className="grid-row">

                        <div className="coluna-2 pad-r">
                            <label className="label-select-ferramenta">Escolha o primeiro componente</label>
                            <select className="select-ferramenta" onChange={sel1}>
                                <option value="">Selecione...</option>
                                {substancias.map(item => (
                                    <option value={item.unifac.substancia?.nome}>{item.unifac.substancia?.nome}</option>
                                ))}
                            </select>
                        </div>

                        <div className="coluna-2 pad-l">
                            <label className="label-select-ferramenta">Escolha o segundo componente</label>
                            <select className="select-ferramenta" onChange={sel2}>
                                <option value="">Selecione...</option>
                                {substancias.map(item => (
                                    <option value={item.unifac.substancia?.nome}>{item.unifac.substancia?.nome}</option>
                                ))}
                            </select>
                        </div>

                    </div>

                    <div className="grid-row">
                        <p className="alerta-ferramenta">{alerta}</p>
                    </div>

                    {carregado && <div className="grafico-ferramenta">
                        <h4 className="label-titulo-grafico">Coeficiente de Atividade vs Fração Molar do Soluto</h4>

                        <ResponsiveContainer minWidth={350} height={300}>
                            <LineChart data={dataGraph} >
                                <XAxis dataKey="Fração" stroke="#1a1a1a">
                                    <Label value="Fração Molar" offset={-8} position="bottom" fontFamily="Ubuntu" />
                                </XAxis>

                                <YAxis stroke="#1a1a1a" domain={["auto", "auto"]}>
                                    <Label value={tipo == "pressao" ? "Pressão (kPa)" : "Temperatura (K)"} fontFamily="Ubuntu" angle={-90} position="center" dx={-22} />
                                </YAxis>

                                <Tooltip />

                                {tipo == "pressao" && <Line type="monotone" dataKey="BOLP" stroke="#1a1a1a" dot={false}/>}
                                {tipo == "pressao" && <Line type="monotone" dataKey="ORVP" stroke="#ea4e4d" dot={false}/>}

                                {tipo == "temperatura" && <Line type="monotone" dataKey="ORVT" stroke="#ea4e4d" dot={false}/>}
                                {tipo == "temperatura" && <Line type="monotone" dataKey="BOLT" stroke="#1a1a1a" dot={false}/>}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>}

                </div>
            </div>

            <Modal ariaHideApp={false} isOpen={aberto} style={modalStyles} onRequestClose={() => setAberto(false)}>

                <div className="form-ferramenta">
                    <label>Tipo de Gráfico</label>
                    <select onChange={seltipo} value={tipo}>
                        <option value="temperatura">Temperatura vs Fração Molar</option>
                        <option value="pressao">Pressão vs Fração Molar</option>
                    </select>
                </div>

                {tipo == "pressao" && <div className="form-ferramenta">
                    <label>Temperatura (K)</label>
                    <input value={temperatura} type="number" onChange={alterarTemperatura} />
                </div>}

                {tipo == "temperatura" && <div className="form-ferramenta">
                    <label>Pressão (kPa)</label>
                    <input value={pressao} type="number" onChange={alterarPressao} />
                </div>}
                
                <div className="grid-row">
                    <p className="alerta-ferramenta">{alerta}</p>
                </div>
            </Modal>

        </div>
    );
}

const modalStyles = {
    overlay: {
        backgroundColor: "rgba(250,250,250,0.3)"
    },
    content: {
        backgroundColor: "#f0f0f5",
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        transform: "translate(-50%, -50%)",
        maxWidth: 600,
        width: "90%",
        borderRadius: 10
    }
};